import { useMemo } from "react";
import { useSelectableMetrics } from "@/hooks/api/use-metrics-definitions";
import { useSalespersonColumnSelection } from "@/hooks/use-column-selection";
import { getSalespersonColumns } from "@/pages/salespeople/salespeople-list/salesperson-columns";
import type { SalespersonMetrics } from "@/types/salesperson-types";
import {
  EntityColumnSelector,
  type SelectableMetric,
} from "./entity-column-selector";

interface SalespeopleColumnSelectorProps {
  selectedMetrics?: Array<keyof SalespersonMetrics>;
  onSelectMetrics?: (metrics: Array<keyof SalespersonMetrics>) => void;
  showMetricsSection?: boolean;
  defaultMetrics?: Array<keyof SalespersonMetrics>;
}

/**
 * Component for managing salesperson column selection including both core and custom fields,
 * and optionally metrics. Displays a popover with checkboxes for selecting which columns to display.
 */
export function SalespeopleColumnSelector({
  selectedMetrics = [],
  onSelectMetrics,
  showMetricsSection = false,
  defaultMetrics = [],
}: SalespeopleColumnSelectorProps) {
  // Get the core columns
  const coreColumns = getSalespersonColumns();

  // Use the salesperson column selection hook (salespeople don't have custom fields)
  const { availableColumns, isColumnSelected, toggleColumn, isLoading, error } =
    useSalespersonColumnSelection(coreColumns);

  // Filter out metric columns from availableColumns since they're handled separately
  const filteredAvailableColumns = useMemo(() => {
    return availableColumns.filter((column) => column.columnType !== "metric");
  }, [availableColumns]);

  // Fetch metrics using the new hook
  const { data: fetchedMetrics } = useSelectableMetrics("salespeople");

  // Use fetched metrics if available, otherwise empty array
  const availableMetrics = useMemo<SelectableMetric[]>(() => {
    if (!showMetricsSection) return [];
    return fetchedMetrics || [];
  }, [showMetricsSection, fetchedMetrics]);

  // Convert selected metrics to string array for the EntityColumnSelector
  const selectedMetricIds = useMemo(() => {
    return selectedMetrics.map((metric) => String(metric));
  }, [selectedMetrics]);

  // Handle metrics selection
  const handleSelectMetrics = (metricIds: string[]) => {
    if (onSelectMetrics) {
      onSelectMetrics(metricIds as Array<keyof SalespersonMetrics>);
    }
  };

  return (
    <EntityColumnSelector
      availableColumns={filteredAvailableColumns}
      isColumnSelected={isColumnSelected}
      toggleColumn={toggleColumn}
      isLoading={isLoading}
      error={error}
      buttonLabel="Columns & Metrics"
      noColumnsMessage="No columns available."
      errorMessage="Error loading columns."
      // Metrics props
      availableMetrics={availableMetrics}
      selectedMetrics={selectedMetricIds}
      onSelectMetrics={handleSelectMetrics}
      showMetricsSection={showMetricsSection}
      defaultMetrics={defaultMetrics}
    />
  );
}
